import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { OrganizationDomainResources } from '../types/models/OrganizationDomainResources';
import type { ApiResponseOrganizationDomainResources } from '../types/api/ApiResponseOrganizationDomain';

interface UseOrganizationDomainResourcesReturn {
  resources: OrganizationDomainResources | null;
  loading: boolean;
  error: string | null;
  notFound: boolean;
  refetch: () => Promise<void>;
}

const NOT_FOUND_PATTERN = /not found/i;

/**
 * Enumerates everything Ancalagon hosts for an organization domain
 * (GET /organization/domains/{id}/resources — the "Delete resources" wizard's inventory).
 *
 * JSend contract: success is `body.status === 'success'` with the `resources` data key; anything else
 * is surfaced through `error`. A missing domain (a JSend error whose message says it was not found) is
 * reported through `notFound`; an HTTP 404 is the dispatcher's "route not found" (endpoint not deployed)
 * and stays an error. A non-JSON body (proxy error page) never surfaces the parser's own error text. On a refetch failure the previous `resources` are kept so the wizard stays
 * mounted; callers reconcile their selection whenever `resources` changes.
 */
export const useOrganizationDomainResources = (id: number): UseOrganizationDomainResourcesReturn => {
  const [resources, setResources] = useState<OrganizationDomainResources | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchResources = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setNotFound(false);

      if (!Number.isInteger(id) || id <= 0) {
        setNotFound(true);
        return;
      }

      const response = await apiFetch(`/organization/domains/${id}/resources`);

      // The backend answers a missing/foreign domain with a JSend error at HTTP 200; an HTTP 404 here can
      // only be the dispatcher's "route not found" — the endpoint is not deployed on this backend.
      if (response.status === 404) {
        setError('The resources endpoint is not available on this backend (HTTP 404).');
        return;
      }

      const body = (await response.json().catch(() => null)) as ApiResponseOrganizationDomainResources | null;
      if (body?.status === 'success' && body.data?.resources) {
        setResources(body.data.resources);
        return;
      }

      const statusText = response.statusText ? ` ${response.statusText}` : '';
      const message = body?.message ?? `Failed to list the domain's resources (HTTP ${response.status}${statusText})`;
      if (NOT_FOUND_PATTERN.test(message)) {
        setNotFound(true);
        return;
      }
      setError(message);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred while listing the domain's resources");
    } finally {
      setLoading(false);
    }
  }, [apiFetch, id]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      void fetchResources();
    }
  }, [auth.isAuthenticated, fetchResources]);

  return { resources, loading, error, notFound, refetch: fetchResources };
};
